import { readEntity } from './apiActions';
import { selectEntity, createEntity, updateEntity, deleteEntity,
  setActivePage, setActiveFilters, setActiveSort, setActiveSearch } from './coreApiActions';
import { displayError, displayErrorMessage, displayErrorMessages, displaySuccessMessage, clearMessages } from './messagesActions';

/**
 * Creates a set of common entity actions
 * @param {string} entity - the entity type the actions should be created for
 * @param {string|Array|object} includes - the includes to pass to read/select entity
 * @param {string|number} id - the id of the entity to load (for member specific containers)
 * @param {string|number} idParam - the param to use for an id (for member specific containers)
 * @param {object} fields - the fields to pass to read/select entity
 * @param {string} basePath - overrides the base path for all of the actions
 * @param {string} host - overrides the host for all of the actions
 * @param {string} token - sends an access token with all of the actions
 * @param {object} config - set additional configuration options through props named after the returned actions
 * @returns {object} with the following actions: loadCollection, selectMember, createMember, updateMember, deleteMember
 */
export function genericEntityActionCreators({ entity, includes, fields, basePath, host, token, ...config } = {}, props = {}) {

  const { params = {}, router = {} } = props;

  function getMemberId(id) {
    if (typeof id !== 'undefined') {
      return id;
    } else if (props.id) {
      return props.id;
    } else if (props.path) {
      const pathParam = /:(\w+)/i.exec(props.path);
      if (pathParam) {
        return params[pathParam[1]];
      }
    }
  }

  /**
   * Action used for loading a collection of entities using readEntity
   * @param {object} filter - key value pair of filters to add to the request
   * @param {object|Array} sorts - array of sorts objects { value: string, direction: 'asc|desc' } to add to the request
   * @param {number|object} page - page number with default page size or object { number: number, size: number } to add to the request
   * @param {object} query - query fields { name: { term: string, fields: { field1: match_type, field2: match_type, ... } } } to add to the request
   */
  function loadCollection({ filters, sorts, page, query } = {}) {
    return readEntity({
      entity,
      includes,
      filters,
      sorts,
      page,
      query,
      fields,
      basePath,
      host,
      token
    });
  }

  /**
   * Action used for reading and selecting an entity using selectEntity
   * @param {object} filter - key value pair of filters to add to the request
   * @param {object|Array} sorts - array of sorts objects { value: string, direction: 'asc|desc' } to add to the request
   * @param {number|object} page - page number with default page size or object { number: number, size: number } to add to the request
   * @param {object} query - query fields { name: { term: string, fields: { field1: match_type, field2: match_type, ... } } } to add to the request
   */
  function selectMember(id) {
    return async (dispatch) => {
      dispatch(selectEntity(null));
      id = getMemberId(id);
      const selectMemberConfig = config.selectMember || {};
      if (selectMemberConfig.updateRoute) {
        const pathname = `/${id || ''}`;
        if (router.location.pathname !== pathname) {
          router.push(pathname);
        }
      }
      dispatch(
        selectEntity({
          id,
          entity,
          includes,
          fields,
          basePath,
          host,
          token
        })
      );
    }
  }
  
  /**
   * Action used for creating a new entity using createEntity
   * @param {JsonApiDecorator} member - entity to create 
   * @param {boolean} addAnother - if true, 'add' will be selected after save, else null will be selected
   */
  function createMember(member, addAnother) {
    const createMemberConfig = config.createMember || {};
    if (createMemberConfig.preprocess) {
      member = createMemberConfig.preprocess(member);
    }

    const afterSave = (dispatch, getState, entity) => {
      dispatch(selectEntity({
        id: addAnother ? 'add' : null,
        entity
      }));
      if (typeof createMemberConfig.afterSave === 'function') {
        createMemberConfig.afterSave(dispatch, getState, entity, router);
      }
    }
    return createEntity({ member, basePath, host, token, ...createMemberConfig, afterSave });
  }

  /**
   * Action used for updating an existing entity using updateEntity
   * @param {JsonApiDecorator} member - entity to update
   */
  function updateMember(member) {
    const updateMemberConfig = config.updateMember || {};
    if (updateMemberConfig.preprocess) {
      member = updateMemberConfig.preprocess(member);
    }

    const afterSave = (dispatch, getState, entity) => {
      dispatch(selectEntity({
        id: null,
        entity
      }));
      if (typeof updateMemberConfig.afterSave === 'function') {
        updateMemberConfig.afterSave(dispatch, getState, entity, router);
      }
    }
    return updateEntity({ member, basePath, host, token, ...updateMemberConfig, afterSave });
  }

  /**
   * Action used for deleting an existing entity using deleteEntity
   * @param {JsonApiDecorator} member - entity to delete
   */
  function deleteMember(member) {
    const deleteMemberConfig = config.deleteMember || {};
    if (deleteMemberConfig.preprocess) {
      member = deleteMemberConfig.preprocess(member);
    }

    return deleteEntity({ member, entity, basePath, host, token, ...deleteMemberConfig });
  }
  
  return {
    loadCollection,
    selectMember,
    createMember,
    updateMember,
    deleteMember
  };
}

export const commonActionCreators = {
  setActivePage, setActiveFilters, setActiveSort, setActiveSearch,
  displayError, displayErrorMessage, displayErrorMessages, displaySuccessMessage, clearMessages
}
